import { JsonValue } from '@bufbuild/protobuf';
import { pb } from '@q6cyber/proto2es';
import { UiHelpersService, UiModule } from '@shared/ui';
import { scrollToFirstHighlight } from 'apps/phoenix/src/app/shared/ui/utilities/highlight.utils';
import { PhoenixTrpcClient } from 'apps/phoenix/src/app/utils';
import { Subject, finalize, from, takeUntil } from 'rxjs';

import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  WritableSignal,
  effect,
  inject,
  signal,
} from '@angular/core';

import { TRPCClientError } from '@trpc/client';

import { DirectCollectService } from '../../services/direct-collect.service';
import { MessageAttachmentComponent } from '../message-attachment/message-attachment.component';

@Component({
  selector: 'phoenix-timeline-message-detail',
  imports: [UiModule, MessageAttachmentComponent],
  templateUrl: './timeline-detail-message.component.html',
})
export class TimelineDetailMessageComponent implements OnDestroy {
  @ViewChild('highlightContainer') highlightContainerRef!: ElementRef;
  @ViewChild('topAnchor') topAnchorRef!: ElementRef;

  selectedMessage!: any;
  formattedMessage: JsonValue = {};
  _selectedId!: string;
  destroy$ = new Subject<void>();
  showHighlightButton = false;

  translateLabel = '';

  @Input() set selectedId(data: string) {
    this._selectedId = data;
    this.getMessageData();
  }
  @Input() actorsColors!: any;
  @Input() heightLimit = true;
  private readonly dataReponse: WritableSignal<any> = signal(null);

  uiService = inject(UiHelpersService);
  phoenixTrpc = inject(PhoenixTrpcClient);
  timelineService = inject(DirectCollectService);
  uiHelper = inject(UiHelpersService);

  constructor() {
    effect(() => {
      this.selectedMessage = this.uiHelper.formatResponse(this.dataReponse());
      this.formattedMessage = this.selectedMessage
        ? pb.collect.DirectCollect.fromJson(this.selectedMessage, {
            ignoreUnknownFields: true,
          }).toJson({
            emitDefaultValues: false,
            useProtoFieldName: true,
          })
        : {};
    });
  }

  formatMessageText(text: string) {
    if (!text) return '';

    const formatted = text.replace(/\n/g, '<br>');
    return formatted;
  }

  getMessageData() {
    if (!this._selectedId) return;

    this.timelineService.itemLoading = true;

    from(
      this.phoenixTrpc.directCollect.get.query({
        id: this._selectedId,
        searchTerm: this.timelineService.usedFilters$().query,
      })
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.timelineService.itemLoading = false;
        })
      )
      .subscribe({
        next: data => {
          this.dataReponse.set(data);
          const hasHighlight = !!(
            data?.highlight && Object.keys(data.highlight).length > 0
          );

          this.translateLabel = data?.node?.textLanguage ?? 'Original';
          this.showHighlightButton = hasHighlight;

          setTimeout(() => {
            this.topAnchorRef?.nativeElement?.scrollIntoView({
              behavior: 'auto',
              block: 'start',
            });
          }, 0);
        },
        error: error => {
          this.dataReponse.set(null);
          const message =
            error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND'
              ? 'Direct Collect not found'
              : 'Error loading Direct Collect!';
          this.uiHelper.handleErrorMessage(error, message);
        },
      });
  }

  get message(): pb.collect.Message | undefined {
    return this.selectedMessage?.message;
  }

  scrollToFirstHighlight = () => {
    const container = this.highlightContainerRef?.nativeElement;
    if (!container) return;

    scrollToFirstHighlight(container);
  };

  hasTranslations(): boolean {
    return !!this.selectedMessage?.translation;
  }

  get formattedMessageText(): string {
    const showTranslations = this.timelineService.showTranslations$();
    if (showTranslations && this.selectedMessage?.translation) {
      return this.formatMessageText(this.selectedMessage.translation);
    }
    return this.formatMessageText(this.message?.text ?? '');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
